/* eslint-disable no-restricted-imports */
import { useCallback } from 'react';
import styled from 'styled-components';
import { useOpenDialog } from '../../routing/ModalRoute';
import { DIALOGS } from '../../routing/constants';
import { getIsMobile } from '../../utils/getIsMobile';
import { ymTrackClickToInstall } from '../../utils/metrika';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useTranslation } from '../../utils/useTranslation';
import { APP_DOWNLOAD_LINK } from '../discovery/AppDiscoveryMobile';
import { IconBookmark } from '../icon/IconBookmark';
import { IconBookmarkFilled } from '../icon/IconBookmarkFilled';
import { IconLike } from '../icon/IconLike';
import { IconShare } from '../icon/IconShare';
import { IconShoppingBagAdd } from '../icon/IconShoppingBagAdd';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex } from '../layout/Flex';
import { Step } from '../users/PromoteShowInFeedDrawer/types';
import { CollectionStatus, CollectionUI } from '../users/types';

const Container = styled(Box)
    .withConfig({ displayName: 'Container' })
    .attrs({
        width: { xs: '100%', sm: 'auto' },
        backgroundColor: { xs: 'var(--background-primary-blur)', sm: 'var(--background-primary)' },
        backdropFilter: { xs: 'blur(25px)', sm: 'none' },
        transform: { xs: 'translate3d(0, 0, 0)', sm: 'translate3d(-50%, 0, 0)' },
        borderRadius: { xs: 0, sm: '1000px' },
        left: { xs: 0, sm: '50%' },
        bottom: { xs: 0, sm: 2 },
        zIndex: 1,
        boxShadow: { xs: 'none', sm: '0px 1px 2px var(--control-secondary-background)' },
    })`
    transition-property: background-color, backdrop-filter;
    transition-duration: 250ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    box-sizing: border-box;
    position: absolute;
`;

const ContentContainer = styled(Flex)
    .withConfig({ displayName: 'ContentContainer' })
    .attrs({
        direction: 'row',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: { xs: 2, sm: 1 },
        spacing: 0.5,
    })``;

const TextFreeButton = styled(Box).withConfig({ displayName: 'TextFreeButton' }).attrs({
    backgroundColor: 'var(--control-secondary-background)',
    p: 1.5,
    borderRadius: '100%',
})``;

const getButtons = (
    collection: CollectionUI,
    onDownloadApp: VoidFunction,
    onShare?: VoidFunction,
    onReaction?: VoidFunction,
    onShowAddPostDialog?: VoidFunction,
    onRemoveSaved?: VoidFunction,
    onSave?: VoidFunction,
) => {
    const t = useTranslation();
    const { isMobile } = getIsMobile();

    const openDialog = useOpenDialog();

    const isCreateCollectionEnabled =
        String(useExperimentFlag('create_collection_from_plus_enabled')) === '1';
    const showInFeedEnabled = String(useExperimentFlag('show_in_feed_enabled')) === '1';

    const promoteInFeedEnabled = showInFeedEnabled && collection.promoteShowInFeedAvailable;

    const openCreateCollection = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    const onPromoteInFeed = useCallback(() => {
        openDialog(DIALOGS.PROMOTE_SHOW_IN_FEED_COLLECTION_DIALOG_NAME, {
            collectionId: collection.id,
            step: Step.CHOOSE_NAME,
        });
    }, [collection.id, openDialog]);

    const isPublic = collection.status === CollectionStatus.PUBLISHED && collection.isPublic;

    const shareButton = (
        <Button
            size="l"
            color="secondary"
            width={promoteInFeedEnabled ? 48 : '100%'}
            spacing={1}
            onClick={onShare}
        >
            <IconShare size={24} />
            {!promoteInFeedEnabled && t('Поделиться')}
        </Button>
    );

    const addPostButton = (
        <Button
            size="l"
            color="secondary"
            minWidth={promoteInFeedEnabled ? undefined : 175}
            width={promoteInFeedEnabled ? 48 : '100%'}
            spacing={1}
            onClick={onShowAddPostDialog}
            dataTestId={DataTestId.ADD_POST_BUTTON}
        >
            <IconShoppingBagAdd size={24} />
            {!promoteInFeedEnabled && t('Добавить товар')}
        </Button>
    );

    const createCollectionButton = (
        <Button width="100%" minWidth={170} size="l" spacing={1} onClick={openCreateCollection}>
            {t('Создать коллекцию')}
        </Button>
    );

    const promoteInFeedButton = (
        <Button size="l" minWidth={155} width="100%" spacing={1} onClick={onPromoteInFeed}>
            {t('Добавить в ленту')}
        </Button>
    );

    const downloadAppButton = (
        <Box width="100%" mr={0.5}>
            <Button color="primary" width="100%" height={48} spacing={1} onClick={onDownloadApp}>
                {t('Перейти в приложение')}
            </Button>
        </Box>
    );

    const onBookmark = useCallback(() => {
        if (collection.isSaved) {
            onRemoveSaved?.();
        } else {
            onSave?.();
        }
    }, [collection.isSaved, onSave, onRemoveSaved]);

    const bookmarkButton = isCreateCollectionEnabled ? (
        <Button
            color="secondary"
            size="l"
            width={!isCreateCollectionEnabled && !isMobile ? '100%' : '48px'}
            spacing={1}
            onClick={onBookmark}
        >
            {collection.isSaved ? (
                <IconBookmarkFilled
                    fontSize={24}
                    color="var(--color-primary)"
                    dataTestId={DataTestId.FILLED}
                />
            ) : (
                <IconBookmark
                    fontSize={24}
                    color="var(--color-primary)"
                    dataTestId={DataTestId.EMPTY}
                />
            )}
            {!isCreateCollectionEnabled && !isMobile && t('Сохранить')}
        </Button>
    ) : null;

    if (collection.modifiable) {
        return (
            <ContentContainer>
                {isPublic ? shareButton : null}
                {addPostButton}
                {promoteInFeedEnabled ? promoteInFeedButton : null}
            </ContentContainer>
        );
    }

    if (isMobile) {
        return (
            <ContentContainer>
                <TextFreeButton onClick={onReaction} dataTestId={DataTestId.LIKE}>
                    <IconLike
                        size={24}
                        strokeWidth="2px"
                        stroke="var(--color-primary)"
                        fill={collection.isLiked ? 'var(--color-primary)' : 'transparent'}
                        dataTestId={collection.isLiked ? DataTestId.FILLED : DataTestId.EMPTY}
                    />
                </TextFreeButton>
                {bookmarkButton}
                <TextFreeButton onClick={onShare} mr={0.5}>
                    <IconShare size={24} fill="var(--color-primary)" />
                </TextFreeButton>
                {isCreateCollectionEnabled ? createCollectionButton : null}
                {!isCreateCollectionEnabled ? downloadAppButton : null}
            </ContentContainer>
        );
    }

    return (
        <ContentContainer>
            <Button
                color="secondary"
                size="l"
                width={isCreateCollectionEnabled ? 48 : '100%'}
                spacing={1}
                onClick={onReaction}
                dataTestId={DataTestId.LIKE}
            >
                <IconLike
                    size={24}
                    strokeWidth="2px"
                    stroke="var(--color-primary)"
                    fill={collection.isLiked ? 'var(--color-primary)' : 'transparent'}
                    dataTestId={collection.isLiked ? DataTestId.FILLED : DataTestId.EMPTY}
                />
                {!isCreateCollectionEnabled && t('Нравится')}
            </Button>
            {bookmarkButton}
            <Button
                color="secondary"
                size="l"
                width={isCreateCollectionEnabled ? 48 : undefined}
                spacing={1}
                onClick={onShare}
                dataTestId={DataTestId.SHARE_BUTTON}
            >
                <IconShare size={24} />
                {!isCreateCollectionEnabled && t('Поделиться')}
            </Button>
            {isCreateCollectionEnabled ? createCollectionButton : null}
        </ContentContainer>
    );
};

interface StickyFooterProps {
    collection: CollectionUI;
    onShare?: VoidFunction;
    onReaction?: VoidFunction;
    onShowAddPostDialog?: VoidFunction;
    onRemoveSaved?: VoidFunction;
    onSave?: VoidFunction;
}

export const StickyFooter = (props: StickyFooterProps) => {
    const { collection, onShare, onReaction, onShowAddPostDialog, onRemoveSaved, onSave } = props;

    const onDownloadClick = useCallback(() => {
        ymTrackClickToInstall();
        window.open(APP_DOWNLOAD_LINK, '_blank');
    }, []);

    return (
        <Container>
            {getButtons(
                collection,
                onDownloadClick,
                onShare,
                onReaction,
                onShowAddPostDialog,
                onRemoveSaved,
                onSave,
            )}
        </Container>
    );
};
